import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { apiRequest, apiGet } from '../../../utils/fetch';
import { AdSpaceCouponCodeInterface, GenericResponseInterface, AdSpaceInterface } from '../../../interfaces/interfaces';

const initialCode: Partial<AdSpaceCouponCodeInterface> = {
  adSpaceId: undefined,
  code: '',
  discountValue: 0,
  discountType: 'percentage',
  maxUses: null,
  currentUses: 0,
  maxUsesPerUser: 1,
  validFrom: null,
  validUntil: null,
  active: true,
  description: '',
  generatedBy: 'admin',
  minimumPurchaseAmount: null,
  combinableWithOthers: true,
  metadata: null,
};

const AdSpaceCouponCodesAdminAdd: React.FC = () => {
  const [code, setCode] = useState<Partial<AdSpaceCouponCodeInterface>>(initialCode);
  const [adSpaces, setAdSpaces] = useState<AdSpaceInterface[]>([]);
  const [selectedAdSpace, setSelectedAdSpace] = useState<AdSpaceInterface | null>(null);
  const [validFrom, setValidFrom] = useState<Dayjs | null>(null);
  const [validUntil, setValidUntil] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchAdSpaces = async () => {
      try {
        const result = await apiGet<GenericResponseInterface<AdSpaceInterface[]>>('/adSpaces');
        if (result.status && result.data) {
          setAdSpaces(result.data);
        }
      } catch (err: any) {
        console.error('Failed to fetch adSpaces:', err);
      }
    };
    fetchAdSpaces();
  }, []);

  const handleChange = (field: keyof AdSpaceCouponCodeInterface, value: any) => {
    setCode(c => ({ ...c, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!code.adSpaceId) {
      setError('Please select an AdSpace');
      setDialogOpen(true);
      return;
    }

    try {
      const payload = {
        ...code,
        validFrom: validFrom?.toISOString(),
        validUntil: validUntil?.toISOString(),
      };

      const result = await apiRequest<GenericResponseInterface<AdSpaceCouponCodeInterface>>('/adSpaceCouponCodes/insert', 'POST', payload);
      if (result.status) {
        setSuccess(result.message || 'AdSpace coupon code created successfully!');
        setDialogOpen(true);
        setCode(initialCode);
        setSelectedAdSpace(null);
        setValidFrom(null);
        setValidUntil(null);
      } else {
        setError(result.message || 'Failed to create coupon code');
        setDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create coupon code');
      setDialogOpen(true);
    }
  };

  const discountTypeHelperText = code.discountType === 'percentage' 
    ? 'Enter 10 for 10% discount' 
    : 'Enter actual amount (e.g., 30 for $30 off)';

  return (
    <Paper sx={{ p: 3, maxWidth: 700, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add New AdSpace Coupon Code</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <Autocomplete
          options={adSpaces}
          getOptionLabel={(option) => `${option.id} - ${option.geoName}`}
          value={selectedAdSpace}
          onChange={(_, value) => {
            setSelectedAdSpace(value);
            handleChange('adSpaceId', value?.id);
          }}
          renderInput={(params) => <TextField {...params} label="Ad Space" required margin="dense" />}
        />
        
        <TextField
          label="Code"
          value={code.code ?? ''}
          onChange={e => handleChange('code', e.target.value.toUpperCase())}
          fullWidth
          margin="dense"
          required
          helperText="Discount code (will be converted to uppercase)"
        />

        <FormControl fullWidth margin="dense">
          <InputLabel id="discount-type-label">Discount Type</InputLabel>
          <Select
            labelId="discount-type-label"
            value={code.discountType ?? 'percentage'}
            label="Discount Type"
            onChange={e => handleChange('discountType', e.target.value)}
          >
            <MenuItem value="percentage">Percentage</MenuItem>
            <MenuItem value="fixed_amount">Fixed Amount</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Discount Value"
          type="number"
          value={code.discountValue ?? 0}
          onChange={e => handleChange('discountValue', parseFloat(e.target.value))}
          fullWidth
          margin="dense"
          required
          helperText={discountTypeHelperText}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <DatePicker
              label="Valid From"
              value={validFrom}
              onChange={(newValue) => setValidFrom(newValue)}
              slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
            />
            <DatePicker
              label="Valid Until"
              value={validUntil}
              onChange={(newValue) => setValidUntil(newValue)}
              slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
            />
          </Box>
        </LocalizationProvider>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Max Uses (optional)"
            type="number"
            value={code.maxUses ?? ''}
            onChange={e => handleChange('maxUses', e.target.value ? parseInt(e.target.value) : null)}
            fullWidth
            margin="dense"
            helperText="Leave empty for unlimited"
          />
          <TextField
            label="Max Uses Per User"
            type="number"
            value={code.maxUsesPerUser ?? 1}
            onChange={e => handleChange('maxUsesPerUser', parseInt(e.target.value))}
            fullWidth
            margin="dense"
            required
          />
        </Box>

        <TextField
          label="Minimum Purchase Amount (optional)"
          type="number"
          value={code.minimumPurchaseAmount ?? ''}
          onChange={e => handleChange('minimumPurchaseAmount', e.target.value ? parseFloat(e.target.value) : null)}
          fullWidth
          margin="dense"
        />

        <TextField
          label="Description (optional)"
          value={code.description ?? ''}
          onChange={e => handleChange('description', e.target.value)}
          fullWidth
          margin="dense"
          multiline
          rows={2}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="active-label">Active</InputLabel>
            <Select
              labelId="active-label"
              value={code.active !== undefined ? (code.active ? 'yes' : 'no') : 'yes'}
              label="Active"
              onChange={e => handleChange('active', e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel id="combinable-label">Combinable With Others</InputLabel>
            <Select
              labelId="combinable-label"
              value={code.combinableWithOthers !== undefined ? (code.combinableWithOthers ? 'yes' : 'no') : 'yes'}
              label="Combinable With Others"
              onChange={e => handleChange('combinableWithOthers', e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create Coupon Code
          </Button>
        </Box>
      </form>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{success || error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdSpaceCouponCodesAdminAdd;
